import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Download, Mail, Send, Save, ArrowLeft, ChevronRight } from 'lucide-react';
import { homeworkService, wellnessHomeworkService } from '../services/api';
// exportInteractiveFormPdf is used via wellnessHomeworkService.exportInteractiveFormPdf
import QuestionRenderer from '../components/homework/QuestionRenderer';
import InteractiveHomeworkProgress from '../components/homework/InteractiveHomeworkProgress';

const AUTOSAVE_DELAY_MS = 1200;

function InteractiveHomeworkPage() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isWellnessAssignment = location.pathname.includes('/homework/interactive/wellness/');

  const interactiveService = useMemo(() => (
    isWellnessAssignment
      ? {
          getInteractiveForm: wellnessHomeworkService.getInteractiveForm,
          saveInteractiveForm: wellnessHomeworkService.saveInteractiveForm,
          submitInteractiveForm: wellnessHomeworkService.submitInteractiveForm,
          downloadPdf: wellnessHomeworkService.downloadAssignmentPdf,
          emailPdf: wellnessHomeworkService.emailAssignmentPdf,
          exportInteractivePdf: wellnessHomeworkService.exportInteractiveFormPdf,
        }
      : {
          getInteractiveForm: homeworkService.getInteractiveForm,
          saveInteractiveForm: homeworkService.saveInteractiveForm,
          submitInteractiveForm: homeworkService.submitInteractiveForm,
          downloadPdf: homeworkService.downloadHomework,
          emailPdf: homeworkService.emailHomework,
          exportInteractivePdf: homeworkService.exportInteractiveFormPdf,
        }
  ), [isWellnessAssignment]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [assignment, setAssignment] = useState(null);
  const [form, setForm] = useState(null);
  const [session, setSession] = useState(null);
  const [responses, setResponses] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const [submitState, setSubmitState] = useState('idle'); // idle | submitting | success | error
  const [submitError, setSubmitError] = useState(null);
  const [activeSection, setActiveSection] = useState(null);

  const autosaveTimer = useRef(null);
  const latestResponses = useRef({});

  // Load form
  useEffect(() => {
    if (!assignmentId) return;
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await interactiveService.getInteractiveForm(assignmentId);
        if (cancelled) return;
        const { assignment: a, form: f, session: s } = res.data.data;
        setAssignment(a);
        setForm(f);
        setSession(s);
        const initial = s.responses || {};
        setResponses(initial);
        latestResponses.current = initial;
        if (f.sections?.length > 0) {
          setActiveSection(f.sections[0].id);
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || 'Failed to load homework. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [assignmentId, interactiveService]);

  // Autosave logic
  const triggerAutosave = useCallback(() => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(async () => {
      if (!assignmentId) return;
      setSaveState('saving');
      try {
        const res = await interactiveService.saveInteractiveForm(assignmentId, latestResponses.current);
        setSession((prev) => ({
          ...prev,
          status: res.data.data.status,
          responseMeta: res.data.data.responseMeta,
          lastSavedAt: res.data.data.lastSavedAt
        }));
        setSaveState('saved');
      } catch {
        setSaveState('error');
      }
    }, AUTOSAVE_DELAY_MS);
  }, [assignmentId, interactiveService]);

  const handleChange = useCallback((questionId, value) => {
    setResponses((prev) => {
      const updated = { ...prev, [questionId]: value };
      latestResponses.current = updated;
      return updated;
    });
    setValidationErrors((prev) => {
      if (!prev[questionId]) return prev;
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
    setSaveState('idle');
    triggerAutosave();
  }, [triggerAutosave]);

  const handleManualSave = async () => {
    if (!assignmentId) return;
    setSaveState('saving');
    try {
      const res = await interactiveService.saveInteractiveForm(assignmentId, latestResponses.current);
      setSession((prev) => ({
        ...prev,
        status: res.data.data.status,
        responseMeta: res.data.data.responseMeta,
        lastSavedAt: res.data.data.lastSavedAt
      }));
      setSaveState('saved');
    } catch {
      setSaveState('error');
    }
  };

  const handleSubmit = async () => {
    if (!form || !assignmentId) return;

    // Validate required questions
    const errors = {};
    (form.questions || []).forEach((q) => {
      if (!q.required) return;
      const value = latestResponses.current[q.id];
      const isEmpty = Array.isArray(value)
        ? value.length === 0
        : value === null || value === undefined || String(value).trim() === '';
      if (isEmpty) {
        errors[q.id] = 'This question is required.';
      }
    });

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      // Jump to first section with an error
      const firstErrorQuestion = (form.questions || []).find((q) => errors[q.id]);
      if (firstErrorQuestion) {
        setActiveSection(firstErrorQuestion.sectionId);
      }
      return;
    }

    setSubmitState('submitting');
    setSubmitError(null);
    try {
      await interactiveService.submitInteractiveForm(assignmentId, latestResponses.current);
      setSubmitState('success');
      setSession((prev) => ({ ...prev, status: 'completed' }));
    } catch (err) {
      const msg = err.response?.data?.message
        || err.response?.data?.error
        || 'Submission failed. Please try again.';
      setSubmitError(msg);
      setSubmitState('error');
    }
  };

  const handleDownload = async () => {
    try {
      const res = await interactiveService.downloadPdf(assignmentId);
      const fileName = `${assignment?.title || 'homework'}.pdf`;
      const blobUrl = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = blobUrl; link.download = fileName;
      document.body.appendChild(link); link.click();
      document.body.removeChild(link); window.URL.revokeObjectURL(blobUrl);
    } catch { alert('Failed to download PDF. Please try again.'); }
  };

  const handleExportCompleted = async () => {
    try {
      const res = await interactiveService.exportInteractivePdf(assignmentId);
      const fileName = `${assignment?.title || 'homework'}-completed.pdf`;
      const blobUrl = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = blobUrl; link.download = fileName;
      document.body.appendChild(link); link.click();
      document.body.removeChild(link); window.URL.revokeObjectURL(blobUrl);
    } catch { alert('Failed to export completed homework. Please try again.'); }
  };

  const handleEmailPdf = async () => {
    try {
      await interactiveService.emailPdf(assignmentId);
      alert('PDF sent to your email address!');
    } catch {
      alert('Failed to send PDF. Please download it instead.');
    }
  };

  // ─── Rendering ─────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-neutral-500 text-sm">Loading homework...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4">
        <p className="text-red-600 text-sm text-center">{error}</p>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-sm text-primary-700 underline"
        >
          Go back
        </button>
      </div>
    );
  }

  if (submitState === 'success') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 px-6">
        <div className="rounded-full bg-green-100 p-4">
          <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-neutral-900">Homework Submitted!</h2>
        <p className="text-neutral-600 text-center max-w-sm">
          Your responses have been saved. Your care team will be notified.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <button
            type="button"
            onClick={handleExportCompleted}
            className="flex items-center gap-2 rounded-md bg-primary-700 px-4 py-2 text-sm text-white hover:bg-primary-800"
          >
            <Download className="h-4 w-4" />
            Export Completed PDF
          </button>
          <button
            type="button"
            onClick={handleDownload}
            className="flex items-center gap-2 rounded-md border border-neutral-300 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
          >
            <Download className="h-4 w-4" />
            Download Blank PDF
          </button>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 rounded-md border border-neutral-300 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const sections = form?.sections || [];
  const questions = form?.questions || [];
  const activeSectionQuestions = questions.filter((q) => q.sectionId === activeSection);
  const isCompleted = session?.status === 'completed';

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b border-neutral-200 bg-white px-4 py-3 shadow-sm">
        <div className="mx-auto max-w-2xl">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleDownload}
                title="Download PDF"
                className="rounded-md border border-neutral-300 p-2 text-neutral-600 hover:bg-neutral-50"
              >
                <Download className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={handleEmailPdf}
                title="Email PDF"
                className="rounded-md border border-neutral-300 p-2 text-neutral-600 hover:bg-neutral-50"
              >
                <Mail className="h-4 w-4" />
              </button>
              {!isCompleted && (
                <button
                  type="button"
                  onClick={handleManualSave}
                  disabled={saveState === 'saving'}
                  className="flex items-center gap-1 rounded-md border border-neutral-300 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />
                  {saveState === 'saving' ? 'Saving...' : saveState === 'saved' ? 'Saved' : 'Save'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="mx-auto max-w-2xl px-4 py-6 space-y-6">
        {/* Title and progress */}
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">{assignment?.title || form?.title}</h1>
          {assignment?.dueDate && (
            <p className="mt-1 text-sm text-neutral-500">
              Due: {new Date(assignment.dueDate).toLocaleDateString()}
            </p>
          )}
          <div className="mt-4">
            <InteractiveHomeworkProgress
              status={session?.status || 'not_started'}
              completionPercent={session?.responseMeta?.completionPercent || 0}
              lastSavedAt={session?.lastSavedAt}
            />
          </div>
        </div>

        {/* Section tabs */}
        {sections.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {sections.map((section) => {
              const hasError = questions
                .filter((q) => q.sectionId === section.id)
                .some((q) => validationErrors[q.id]);
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => setActiveSection(section.id)}
                  className={`flex-shrink-0 flex items-center gap-1 rounded-full px-4 py-1.5 text-sm transition-colors
                    ${activeSection === section.id
                      ? 'bg-primary-700 text-white'
                      : hasError
                        ? 'bg-red-50 text-red-600 border border-red-300'
                        : 'bg-white text-neutral-700 border border-neutral-300 hover:border-primary-400'
                    }`}
                >
                  {section.title}
                  {hasError && <span className="text-red-400">·</span>}
                </button>
              );
            })}
          </div>
        )}

        {/* Section description */}
        {activeSection && sections.length > 0 && (
          () => {
            const section = sections.find((s) => s.id === activeSection);
            return section?.description ? (
              <p className="text-sm text-neutral-600">{section.description}</p>
            ) : null;
          }
        )()}

        {/* Questions */}
        <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
          {activeSectionQuestions.length === 0 ? (
            <p className="text-sm text-neutral-500">No questions in this section.</p>
          ) : (
            activeSectionQuestions.map((question) => (
              <QuestionRenderer
                key={question.id}
                question={question}
                value={responses[question.id]}
                onChange={(val) => handleChange(question.id, val)}
                error={validationErrors[question.id]}
              />
            ))
          )}
        </div>

        {/* Section navigation */}
        {sections.length > 1 && (
          <div className="flex justify-between">
            {(() => {
              const idx = sections.findIndex((s) => s.id === activeSection);
              const prev = idx > 0 ? sections[idx - 1] : null;
              const next = idx < sections.length - 1 ? sections[idx + 1] : null;
              return (
                <>
                  {prev ? (
                    <button
                      type="button"
                      onClick={() => setActiveSection(prev.id)}
                      className="flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900"
                    >
                      <ArrowLeft className="h-4 w-4" /> {prev.title}
                    </button>
                  ) : <span />}
                  {next && (
                    <button
                      type="button"
                      onClick={() => setActiveSection(next.id)}
                      className="flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900"
                    >
                      {next.title} <ChevronRight className="h-4 w-4" />
                    </button>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* Submit area */}
        {!isCompleted && (
          <div className="space-y-3 pt-2">
            {submitError && (
              <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
                {submitError}
              </div>
            )}
            {Object.keys(validationErrors).length > 0 && (
              <div className="rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-700">
                Please complete all required questions before submitting.
              </div>
            )}
            {saveState === 'error' && (
              <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-600">
                Autosave failed. Please save manually before submitting.
              </div>
            )}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitState === 'submitting'}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary-700 px-6 py-3 text-sm font-semibold text-white hover:bg-primary-800 disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              {submitState === 'submitting' ? 'Submitting...' : 'Submit Homework'}
            </button>
          </div>
        )}

        {isCompleted && (
          <div className="space-y-3">
            <div className="rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700 text-center">
              This homework was completed on{' '}
              {session?.submittedAt ? new Date(session.submittedAt).toLocaleDateString() : 'an earlier date'}.
            </div>
            <button
              type="button"
              onClick={handleExportCompleted}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-primary-600 px-6 py-3 text-sm font-semibold text-primary-700 hover:bg-primary-50"
            >
              <Download className="h-4 w-4" />
              Export Completed PDF
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default InteractiveHomeworkPage;
